import { createSlice } from "@reduxjs/toolkit";
import LoadingStatus from "enums/loadingStatus";
import initialState from "./initialState";
import getDefaultProductType from "../services/productTypeService";
import { hasAvailableNow, hasUnderConstruction } from "../services/homeStatusValidator";
import SpecStatus from "../enums/specStatus";
import getDefaultResultType from "../services/resultTypeService";
import LocationType from "../enums/locationType";
import { facetsSelector } from "./selectors";
import SortType from "../enums/sortType";
import ResultType from "../enums/resultType";

export const searchParametersSlice = createSlice({
  name: "searchParameters",
  initialState,
  reducers: {
    fetchContentTemplateType: (state, action) => {
      state.contentTemplateType = action.payload;
    },
    fetchBuilderReviews: (state, action) => {
      state.facts.builderReviews = action.payload;
      state.results.paging.page = 1;
    },
    fetchConsumerPromos: (state, action) => {
      state.facts.consumerPromos = action.payload;
      state.results.paging.page = 1;
    },
    fetchPriceReduction: (state, action) => {
      state.facts.priceReduction = action.payload;
      state.results.paging.page = 1;
    },
    fetchHomeOwnersAssociationFee: (state, action) => {
      state.facts.hasHomeOwnersAssociationFee = action.payload;
      state.results.paging.page = 1;
    },
    fetchActiveAdult: (state, action) => {
      state.features.adult = action.payload;
      state.features.ageRestricted = action.payload;

      // toggle the excludes
      state.resultsModifiers.excludeAdult = !action.payload;
      state.resultsModifiers.excludeAgeRestricted = !action.payload;

      // reset the page
      state.results.paging.page = 1;
    },
    fetchAnyCommunities: (state) => {
      // toggle off the excludes
      state.resultsModifiers.excludeAdult = false;
      state.resultsModifiers.excludeAgeRestricted = false;

      // toggle off the includes
      state.features.adult = false;
      state.features.ageRestricted = false;

      // reset the page
      state.results.paging.page = 1;
    },
    fetchExcludeActiveAdult: (state, action) => {
      state.resultsModifiers.excludeAdult = action.payload;
      state.resultsModifiers.excludeAgeRestricted = action.payload;

      // toggle the includes
      state.features.adult = !action.payload;
      state.features.ageRestricted = !action.payload;

      // reset the page
      state.results.paging.page = 1;
    },
    fetchResultsListingError: (state) => {
      state.fetchResultListingsStatus = LoadingStatus.Failed;
    },
    fetchResultFacetsError: (state) => {
      state.counts.brands = [];
      state.fetchBrandsFacetingStatus = LoadingStatus.Failed;
    },
    fetchPageSize: (state, action) => {
      state.results.paging.pageSize = action.payload;
      state.results.paging.page = 1;
    },
    fetchPageNumber: (state, action) => {
      state.results.paging.page = action.payload;
    },
    fetchPagingControlsPrev: (state) => {
      const newPage = parseInt(state.results.paging.page, 10) - 1;
      state.results.paging.page = newPage;
    },
    fetchPagingControlsNext: (state) => {
      const newPage = parseInt(state.results.paging.page, 10) + 1;
      state.results.paging.page = newPage;
    },
    fetchBathrooms: (state, action) => {
      const value = action.payload !== "0" ? action.payload : null;
      state.facts.minimumBathroom = value;
      state.results.paging.page = 1;
    },
    fetchBedrooms: (state, action) => {
      const value = action.payload !== "0" ? action.payload : null;
      state.facts.minimumBedroom = value;
      state.results.paging.page = 1;
    },
    fetchLocations: (state, action) => {
      // Here we have to assign each property separately because it affects when
      // location parameter is compared against itself. If the spread operator is used, it creates
      // a new object so the location property comparison fails when its values are the same.
      state.location.locationType = LocationType.fromKey(action.payload.locationType)?.toString();
      state.location.marketId = action.payload.marketId;
      state.location.stateName = action.payload.stateName;
      state.location.marketState = action.payload.marketState;
      state.location.marketName = action.payload.marketName;
      state.location.marketStateName = action.payload.marketStateName;
      state.location.geoLocationState = action.payload.geoLocationState;
      state.location.city = action.payload.city;
      state.location.neighborhood = action.payload.neighborhoodName;
      state.location.schoolDistrictName = action.payload.schoolDistrictName;
      state.location.schoolDistrictId = action.payload.schoolDistrictId;
      state.location.county = action.payload.county;
      state.location.zip = action.payload.zip;
      state.location.originLatitude = action.payload.originLatitude;
      state.location.originLongitude = action.payload.originLongitude;
      state.location.minimumLatitude = action.payload.minimumLatitude;
      state.location.maximumLatitude = action.payload.maximumLatitude;
      state.location.minimumLongitude = action.payload.minimumLongitude;
      state.location.maximumLongitude = action.payload.maximumLongitude;

      state.results.paging.page = 1;

      // Only reset brand filter for genuine location changes.
      // Preserve it for rectangular-area map searches and State SRP viewport
      // fetches (pan/zoom), where the user's active filters should remain applied.
      if (
        state.location.locationType !== LocationType.RectangularArea.toString() &&
        !action.payload.preserveFilters
      ) {
        state.facts.brandId = null;
        state.facts.brandName = "";
      }
    },
    fetchPrice: (state, action) => {
      const { minimumPrice, maximumPrice, maximumPriceLimit } = action.payload;
      const isMaximum = maximumPriceLimit === maximumPrice;
      state.facts.price.minimumPrice = minimumPrice;
      // send null if the maximum value is the maximum value of the slider, to avoid removing
      // listings with a price range higher than the maximum value of the slider.
      state.facts.price.maximumPrice = !isMaximum ? maximumPrice : null;
      state.results.paging.page = 1;
    },
    fetchResultsType: (state, action) => {
      state.product.productType = action.payload.productType;
      state.results.resultType = action.payload.resultType;
      state.results.paging.page = 1;

      const sortType = SortType.fromKey(state.results.sorting.sortBy);
      const resultType = ResultType.fromKey(state.results.resultType);

      if (!sortType?.validResultTypes.includes(resultType)) {
        state.results.sorting = {
          sortBy: null,
        };
      }
    },
    fetchSort: (state, action) => {
      state.results.sorting = {
        sortBy: action.payload.sortBy,
        sortOrder: action.payload.sortOrder,
        sortFirstBy: action.payload.sortFirstBy,
      };
      state.results.paging.page = 1;
    },
    fetchLivingAreas: (state, action) => {
      state.facts.livingAreas = action.payload;
      state.results.paging.page = 1;
    },
    fetchStories: (state, action) => {
      state.facts.stories = action.payload;
      state.results.paging.page = 1;
    },
    fetchPrimaryBedroom: (state, action) => {
      state.facts.primaryBedroom = action.payload;
      state.results.paging.page = 1;
    },
    fetchGarages: (state, action) => {
      state.facts.garages = action.payload;
      state.results.paging.page = 1;
    },
    fetchGolfCourse: (state, action) => {
      state.features.golfCourse = action.payload;
      state.results.paging.page = 1;
    },
    fetchMarketingStatus: (state, action) => {
      state.facts.marketingStatus = action.payload;
      state.results.paging.page = 1;
    },
    fetchAmenities: (state, action) => {
      // assign either the new value or the existing value
      // we need to do this since using the spread operator doesn't
      // work with nested objects when using selectors to get the latest values
      state.features.natureAreas = action.payload.natureAreas;
      state.features.pool = action.payload.pool;
      state.features.golfCourse = action.payload.golfCourse;
      state.features.green = action.payload.green;
      state.features.sportsFacilities = action.payload.sportsFacilities;
      state.features.parks = action.payload.parks;
      state.features.views = action.payload.views;
      state.features.waterfront = action.payload.waterfront;
      state.features.gatedCommunity = action.payload.gatedCommunity;

      state.results.paging.page = 1;
    },
    fetchMoveInDateByStatus: (state, action) => {
      state.facts.homeStatus = action.payload;
      state.facts.moveInDateRange = null;
      state.results.paging.page = 1;
    },
    clearMoveInDateFilter: (state) => {
      state.facts.homeStatus = null;
      state.facts.moveInDateRange = null;
      state.results.paging.page = 1;
    },
    clearFilters: (state) => {
      // resetting the features and facts from the state.
      const productType = getDefaultProductType(state.product.productType, state.results.resultType);
      const resultType = getDefaultResultType(state.results.resultType);
      state.features = {};
      state.resultsModifiers = {};
      state.facts = {
        price: {},
      };
      state.product.productType = productType;
      state.results.resultType = resultType;

      // setting the page as page 1
      state.results.paging.page = 1;

      state.results.sorting = {
        sortBy: null,
      };
    },
    fetchMoveInDateByRange: (state, action) => {
      state.facts.homeStatus = null;
      state.facts.moveInDateRange = action.payload;
      state.results.paging.page = 1;
    },
    fetchResultListings: (state) => {
      state.fetchResultListingsStatus = LoadingStatus.Loading;
    },
    fetchResultCounts: (state) => {
      state.fetchResultCountsStatus = LoadingStatus.Loading;
    },
    fetchBrandsFaceting: (state) => {
      state.fetchBrandsFacetingStatus = LoadingStatus.Loading;
    },
    fetchResultFacetsFulfilled: (state, action) => {
      state.counts.brands = action.payload.Brands;
      state.fetchBrandsFacetingStatus = LoadingStatus.Succeeded;
    },
    fetchResultsCountsFulfilled: (state, action) => {
      state.counts.hotDealsCount = action.payload.HotDealsCount;
      state.counts.comingSoonCount = action.payload.ComingSoonCount;
      state.counts.adultCount = action.payload.AdultCount;
      state.counts.buildablePlansCount = action.payload.BuildablePlansCount;
      state.counts.quickMoveInCount = action.payload.QuickMoveInCount;
      state.counts.multiFamilyCount = action.payload.MultiFamilyCount;
      state.counts.listingCount = action.payload.ListingCount;
      state.counts.spotlightListingsCount = action.payload.SpotlightListingsCount || 0;
      state.fetchResultCountsStatus = LoadingStatus.Succeeded;
    },
    fetchResultsCountsError: (state) => {
      state.counts.listingCount = 0;
      state.counts.spotlightListingsCount = 0;
      state.fetchResultCountsStatus = LoadingStatus.Failed;
    },
    fetchResultsListingFulfilled: (state) => {
      state.fetchResultListingsStatus = LoadingStatus.Succeeded;
    },
    fetchCustom: (state, action) => {
      const { resultType, productType } = action.payload;
      state.features.singleFamily = false;
      state.features.luxury = false;
      state.features.condosAndTownHomes = false;
      state.product.productType = productType;
      state.results.resultType = resultType;
      state.facts.moveInDateRange = null;
      state.facts.homeStatus = null;
      state.results.paging.page = 1;
    },
    fetchSingleFamily: (state, action) => {
      const { resultType, productType, singleFamily } = action.payload;
      state.features.singleFamily = singleFamily;
      state.features.luxury = false;
      state.features.condosAndTownHomes = false;
      state.product.productType = productType;
      state.results.resultType = resultType;
      state.results.paging.page = 1;
    },
    fetchModelHome: (state, action) => {
      const { resultType, productType, homeStatus } = action.payload;
      const newHomeStatus = [homeStatus];

      if (hasAvailableNow(state.facts.homeStatus)) {
        newHomeStatus.push(SpecStatus.AvailableNow.name);
      }

      if (hasUnderConstruction(state.facts.homeStatus)) {
        newHomeStatus.push(SpecStatus.UnderConstruction.name);
      }

      state.facts.homeStatus = newHomeStatus.join(",");
      state.features.singleFamily = false;
      state.features.luxury = false;
      state.features.condosAndTownHomes = false;
      state.product.productType = productType;
      state.results.resultType = resultType;
      state.results.paging.page = 1;
    },
    fetchLuxury: (state, action) => {
      const { resultType, productType, luxury } = action.payload;
      state.features.singleFamily = false;
      state.features.luxury = luxury;
      state.features.condosAndTownHomes = false;
      state.product.productType = productType;
      state.results.resultType = resultType;
      state.results.paging.page = 1;
    },
    fetchCondosAndTownHomes: (state, action) => {
      const { resultType, productType, condosAndTownHomes } = action.payload;
      state.features.singleFamily = false;
      state.features.luxury = false;
      state.features.condosAndTownHomes = condosAndTownHomes;
      state.product.productType = productType;
      state.results.resultType = resultType;
      state.results.paging.page = 1;
    },
    fetchBrandId: (state, action) => {
      const brand = action.payload.brand;
      const brandId = brand.id || null;
      state.facts.brandId = brandId;
      state.facts.brandName = brand.name;
      state.results.paging.page = 1;
    },
    fetchViewBrandResults: (state, action) => {
      const brand = action.payload.brand;
      const brandId = brand.id || null;
      state.facts.brandId = brandId;
      state.facts.brandName = brand.name;
      state.results.resultType = action.payload.resultType;
      state.product.productType = action.payload.productType;
      state.results.paging.page = 1;
    },
    fetchViewBrandMarketResults: (state, action) => {
      const brand = action.payload.brand;
      const brandId = brand.id || null;
      state.facts.brandId = brandId;
      state.facts.brandName = brand.name;
      state.results.resultType = action.payload.resultType;
      state.product.productType = action.payload.productType;
      state.location.locationType = action.payload.locationType;
      state.location.city = '';
      state.location.county = '';
      state.location.zip = '';
      state.results.paging.page = 1;
    },
    fetchRvGarages: (state, action) => {
      state.facts.recreationalVehicleGarage = action.payload;
      state.results.paging.page = 1;
    },
    changeZipSearchtoCitySearch: (state) => {
      state.location.zip = null;
      state.location.locationType = LocationType.City.toString();
    },
    changeNeighborhoodSearchtoCitySearch: (state) => {
      state.location.neighborhood = null;
      state.location.neighborhoodName = null;
      state.location.locationType = LocationType.City.toString();
    },
    changeSchoolDistrictSearchtoMarketSearch: (state) => {
      state.location.schoolDistrictId = null;
      state.location.schoolDistrictName = null;
      state.location.locationType = LocationType.Market.toString();
    },
    clearModelHomeStatusFilter: (state) => {
      state.facts.homeStatus = null;
      state.results.paging.page = 1;
    },
    fetchInteractiveSitePlanResults: (state, action) => {
      state.results.paging.page = 1;
      state.results.resultType = action.payload.resultType;
      state.product.productType = action.payload.productType;
    },
    allOptions: (state, action) => {
      const { resultType, productType } = action.payload;
      const newHomeStatus = [];

      // Reset model home but keep available now and under construction
      if (hasAvailableNow(state.facts.homeStatus)) {
        newHomeStatus.push(SpecStatus.AvailableNow.name);
      }

      if (hasUnderConstruction(state.facts.homeStatus)) {
        newHomeStatus.push(SpecStatus.UnderConstruction.name);
      }

      state.facts.homeStatus = newHomeStatus.length > 0 ? newHomeStatus.join(",") : null;

      state.product.productType = productType;
      state.results.resultType = resultType;
      state.features.singleFamily = false;
      state.features.condosAndTownHomes = false;
      state.features.luxury = false;
      // setting the page as page 1
      state.results.paging.page = 1;
    },
  },
});

const searchParams = {
  searchParameters: initialState,
};

/**
 * getAppliedFilters: this function takes an object containing the current applied filters and
 * transforms it into an array. Each filter is represented with a common name that is easily
 * understandable by other components that need to access these filters.
 * @param {Object} payload represents the an object with the derived filters from Search Parameters.
 * @param {Array} appliedFilters array containing any current applied filter.
 * @returns an array with all the filters applied.
 */
const getAppliedFilters = (payload, appliedFilters) => {
  // Get a list of filters that have been applied and whether to add/remove from the applied filters
  const filters = Object.keys(payload).map((key) => ({ key, add: !!payload[key] }));

  filters.forEach((filter) => {
    const key = filter.key.toLowerCase();
    const isCurrentlyActive = appliedFilters.includes(key);

    // Add the filter to the list of applied filters only if it's not already there
    if (filter.add && !isCurrentlyActive) {
      appliedFilters.push(key);
    }

    // Remove the filter from the list of applied filters only if it's already there
    if (!filter.add && isCurrentlyActive) {
      const indexToRemove = appliedFilters.findIndex((item) => item === key);
      appliedFilters.splice(indexToRemove, 1);
    }
  });

  return appliedFilters;
};

export const facetsSlice = createSlice({
  name: "facetsState",
  initialState: {
    appliedFilters: getAppliedFilters(facetsSelector(searchParams), []).sort(),
  },
  reducers: {
    updateFilters: (state, action) => {
      state.appliedFilters = getAppliedFilters(action.payload, state.appliedFilters);
    },
  },
});
